import {
	ForwardedRef,
	forwardRef,
	memo,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from "react";
import ValidFileUploadTypes, {
	BasicImageFileUploadTypes,
} from "src/constants/validFileUploadTypes";
import getFilteredUploadFileTypes from "src/helpers/fileHelpers/getFilteredUploadFileTypes";
import toastUnsupportedFileTypes from "src/helpers/toastUnsupportedFileTypes";
import useStabilizedCallback from "src/hooks/useStabilizedCallback";
import { UploadAssetPromptHandler, UploadAssetPromptProps } from "./types";

const UploadAssetPrompt = (
	{
		onWebUpload,
		renderChildren,
		allowsMultipleSelection,
		webFileDropArea,
		webFileUploadTypes: validFileUploadTypes = BasicImageFileUploadTypes,
	}: UploadAssetPromptProps,
	ref: ForwardedRef<UploadAssetPromptHandler>,
) => {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [isHoveringImageFile, setIsHoveringImageFile] = useState(false);

	const promptUploadPhotos = useStabilizedCallback(async () => {
		fileInputRef.current?.click();
	}, []);
	const accept = useMemo(() => {
		return Array.from(validFileUploadTypes).join(", ");
	}, [validFileUploadTypes]);
	const Content = (
		<>
			{renderChildren({
				promptUpload: promptUploadPhotos,
				loading: false,
			})}
			<input
				ref={fileInputRef}
				type="file"
				accept={accept}
				multiple={allowsMultipleSelection}
				style={{
					display: "none",
				}}
				onChange={async (e) => {
					const { validFiles, numUnsupportedFiles, unsupportedFileTypes } =
						getFilteredUploadFileTypes(e.target.files, validFileUploadTypes);
					if (numUnsupportedFiles > 0) {
						toastUnsupportedFileTypes({
							numUnsupportedFiles,
							unsupportedFileTypes,
						});
					}
					if (validFiles.length === 0) return;

					onWebUpload?.(validFiles);
				}}
			/>
		</>
	);
	useImperativeHandle(
		ref,
		() => ({
			promptUpload: async (options) => {
				// Source is useless on web
				const { source = "library" } = options ?? {};
				await promptUploadPhotos();
			},
		}),
		[promptUploadPhotos],
	);

	if (!webFileDropArea) return Content;
	return (
		<div
			onDrop={async (event) => {
				event.preventDefault();
				setIsHoveringImageFile(false);
				const { validFiles, numUnsupportedFiles, unsupportedFileTypes } =
					getFilteredUploadFileTypes(event.dataTransfer.files, validFileUploadTypes);
				if (numUnsupportedFiles > 0) {
					toastUnsupportedFileTypes({
						numUnsupportedFiles,
						unsupportedFileTypes,
					});
				}
				if (validFiles.length === 0) return;
				onWebUpload?.(validFiles);
			}}
			onDragOver={(event) => {
				event.preventDefault();
			}}
			onDragEnter={(event) => {
				event.preventDefault();
				if (
					!isHoveringImageFile &&
					event.dataTransfer.items.length > 0 &&
					Array.from(event.dataTransfer.items).some(
						(i) => i.kind === "file" && ValidFileUploadTypes.has(i.type),
					)
				) {
					setIsHoveringImageFile(true);
				}
			}}
			onDragEnd={(event) => {
				event.preventDefault();
			}}
			onDragExit={(event) => {
				event.preventDefault();
				setIsHoveringImageFile(false);
			}}
			onMouseOut={() => {
				if (isHoveringImageFile) setIsHoveringImageFile(false);
			}}
			style={{
				width: "100%",
				height: "100%",
			}}
		>
			{webFileDropArea.render({
				isHovering: isHoveringImageFile,
				children: Content,
			})}
		</div>
	);
};
export default memo(forwardRef(UploadAssetPrompt));
